/**
 * All Contracts Page - Reusable component for contract management
 * Task 45: Contract pages for agencies, consulting, and IT companies
 */

import { useState } from 'react';
import { CompanyType } from '@/types/company';
import { ContractList } from '../shared/ContractList';
import { ContractForm } from '../shared/ContractForm';
import { useIndustryContracts, useIndustryProjects } from '@/hooks/industry';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { IndustryContract } from '@/types/industry';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface AllContractsPageProps {
  domain: CompanyType;
  pageTitle: string;
  pageDescription: string;
}

export function AllContractsPage({ domain, pageTitle, pageDescription }: AllContractsPageProps) {
  const { contracts, isLoading, createContract, updateContract, deleteContract } =
    useIndustryContracts({ domain });

  const { projects } = useIndustryProjects({ domain });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<IndustryContract | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch clients
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      return data;
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients', company?.id],
    queryFn: async () => {
      if (!company) return [];

      const { data } = await supabase
        .from('clients')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_active', true)
        .order('name');

      return data || [];
    },
    enabled: !!company,
  });

  const handleCreate = () => {
    setEditingContract(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (contract: IndustryContract) => {
    setEditingContract(contract);
    setIsFormOpen(true);
  };

  const handleDelete = async (contract: IndustryContract) => {
    if (!window.confirm('Tem certeza que deseja excluir este contrato?')) {
      return;
    }

    try {
      await deleteContract(contract.id);
      toast.success('Contrato excluído com sucesso');
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error('Erro ao excluir contrato');
    }
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      // Build metadata
      const metadata = {
        recurrence: data.recurrence !== 'none' ? data.recurrence : null,
        paymentDay: data.payment_day,
        description: data.description,
        paymentTerms: data.payment_terms,
        clientName:
          data.client_id && clients ? clients.find((c) => c.id === data.client_id)?.name : undefined,
        projectName:
          data.industry_project_id && projects
            ? projects.find((p) => p.id === data.industry_project_id)?.name
            : undefined,
      };

      const contractData = {
        client_id: data.client_id || null,
        industry_project_id: data.industry_project_id || null,
        start_date: data.start_date.toISOString().split('T')[0],
        end_date: data.end_date ? data.end_date.toISOString().split('T')[0] : null,
        value: data.value,
        metadata,
      };

      if (editingContract) {
        await updateContract({
          id: editingContract.id,
          data: contractData,
        });
        toast.success('Contrato atualizado com sucesso');
      } else {
        await createContract(contractData);
        toast.success('Contrato criado com sucesso');
      }

      setIsFormOpen(false);
      setEditingContract(undefined);
    } catch (error) {
      console.error('Error saving contract:', error);
      toast.error('Erro ao salvar contrato');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Carregando contratos...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">{pageTitle}</h1>
        <p className="text-muted-foreground">{pageDescription}</p>
      </div>

      {/* Contract List */}
      <ContractList
        contracts={contracts || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCreate={handleCreate}
      />

      {/* Contract Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContract ? 'Editar' : 'Novo'} Contrato</DialogTitle>
            <DialogDescription>
              {editingContract
                ? 'Atualize as informações do contrato'
                : 'Preencha as informações do novo contrato'}
            </DialogDescription>
          </DialogHeader>
          <ContractForm
            contract={editingContract}
            clients={clients}
            projects={projects?.map((p) => ({ id: p.id, name: p.name })) || []}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingContract(undefined);
            }}
            isSubmitting={isSubmitting}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
